import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { MapPin, CalendarDays, Users, CheckCircle2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { EVENT_CATEGORIES, EVENT_STATUS } from '../lib/eventData';

export default function EventCard({ event }) {
  const { language } = useLanguage();
  const cat = EVENT_CATEGORIES[event.category];
  const status = EVENT_STATUS[event.status] || EVENT_STATUS.open;

  const slots = event.slots || [];
  const totalNeeded = slots.reduce((s, x) => s + (x.count_needed || 0), 0);
  const totalFilled = slots.reduce((s, x) => s + (x.count_filled || 0), 0);

  const dateStr = event.event_date
    ? new Date(event.event_date).toLocaleDateString(language === 'lv' ? 'lv-LV' : language === 'ru' ? 'ru-RU' : 'en-US',
      { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
    : null;

  return (
    <Link to={`/events/${event.id}`} className="group block bg-white border border-[#E5E4E0] rounded-xl p-5 hover:-translate-y-1 hover:shadow-lg transition-all duration-200" data-testid={`event-card-${event.id}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <Badge className="bg-[#F3F2EE] text-[#5C5C5C] border-0 font-figtree">{cat ? cat[language] || cat.lv : event.category}</Badge>
        {event.team_complete && event.status === 'open' ? (
          <Badge className="bg-[#4A6B53] text-white border-0 gap-1" data-testid={`team-complete-${event.id}`}>
            <CheckCircle2 className="w-3 h-3" />{language === 'lv' ? 'Komanda nokomplektēta' : language === 'ru' ? 'Команда укомплектована' : 'Team complete'}
          </Badge>
        ) : (
          <Badge className={`${status.color} text-white border-0`}>{status[language] || status.lv}</Badge>
        )}
      </div>
      <h3 className="font-outfit font-semibold text-[#121212] text-lg mb-2 line-clamp-1 group-hover:text-[#D97736] transition-colors">{event.title}</h3>
      {event.description && <p className="text-[#5C5C5C] text-sm line-clamp-2 mb-4 font-figtree">{event.description}</p>}

      <div className="flex items-center gap-2 text-sm text-[#D97736] font-medium mb-3">
        <Users className="w-4 h-4" />
        {totalFilled}/{totalNeeded} {language === 'lv' ? 'lomas aizpildītas' : language === 'ru' ? 'ролей заполнено' : 'roles filled'}
        <span className="text-[#5C5C5C] font-normal">· {slots.length} {language === 'lv' ? 'lomu veidi' : language === 'ru' ? 'типов ролей' : 'role types'}</span>
      </div>

      <div className="flex items-center justify-between text-xs text-[#5C5C5C] border-t border-[#F3F2EE] pt-3">
        <span className="flex items-center gap-1 min-w-0"><MapPin className="w-3.5 h-3.5 shrink-0" /><span className="truncate">{event.city || event.region}</span></span>
        {dateStr && <span className="flex items-center gap-1 shrink-0"><CalendarDays className="w-3.5 h-3.5" />{dateStr}</span>}
      </div>
      <p className="text-xs text-[#9CA3AF] mt-2">{language === 'lv' ? 'Organizators' : language === 'ru' ? 'Организатор' : 'Organizer'}: {event.organizer_name}</p>
    </Link>
  );
}
